// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, PagingSidePanelType, RowsSkeletonLoader, SidePanel } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Country } from 'types/authentication/Profile'
import { highlightRowBasedOnId } from 'utils/table/table'
import { FiatPayout, PreProcessedPayouts } from '../types'
import { isFiatB2CBatch } from '../utils'
import { MassPayoutSidePanelBody } from './MassPayoutSidePanelBody'

export type MassPayoutSummaryProps = {
  isOpen: boolean
  massPayout: FiatPayout
  preProcessedPayout: PreProcessedPayouts
  pageMassPayout: PagingSidePanelType
  onClose: () => void
  isLoadingMassFiatPayoutDetail: boolean
}

export type SidePanelBodyDataProps = {
  preProcessedPayout: PreProcessedPayouts
  country: string
}

export const MassPayoutSidePanel = ({
  isOpen,
  preProcessedPayout,
  massPayout,
  pageMassPayout,
  onClose,
  isLoadingMassFiatPayoutDetail,
}: MassPayoutSummaryProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const getCountryNameByCode = (code: string, countries: Country[]): string => {
    const foundCountry = countries?.find(country => country?.code === code)
    return foundCountry ? foundCountry?.name : code
  }

  const isB2C = useMemo(() => {
    return isFiatB2CBatch(preProcessedPayout)
  }, [preProcessedPayout])

  const { data: countries } = useCountriesQuery(true)

  const primaryLabel = isB2C
    ? `${preProcessedPayout?.details?.beneficiary?.entity?.firstName} ${preProcessedPayout?.details?.beneficiary?.entity?.lastName}`
    : `${preProcessedPayout?.details?.beneficiary?.entity?.name}`

  const secondaryLabel = isB2C
    ? t('massPayouts.individualBeneficiary')
    : t('massPayouts.businessBeneficiary')

  const iconUrl = `${process.env.ICONS_URL}${massPayout?.currency?.toLowerCase()}.svg`

  const sidePanelBodyData: SidePanelBodyDataProps = {
    preProcessedPayout,
    country: getCountryNameByCode(
      preProcessedPayout?.details?.beneficiary?.entity?.firstName,
      countries
    ),
  }

  return (
    <SidePanel
      id={'side-panel-summary-mass-payout'}
      isOpen={isOpen}
      paging={pageMassPayout}
      onClose={onClose}
      activeRowId={massPayout?.walletId}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <SidePanelHeaderTemplateTwo
          heading={{
            iconUrls: [iconUrl],
            primaryLabel: (
              <Box className="md:flex w-full">
                <h2 className="text-h2 text-foreground-prominent">{primaryLabel}</h2>
              </Box>
            ),
            secondaryLabel: (
              <p className="text-body-regular text-foreground-subdued">{secondaryLabel}</p>
            ),
          }}
          bottomContent={{
            leftLabel: t('accountNumber'),
            leftDescription: preProcessedPayout?.details?.beneficiary?.bankAccount
              ?.accountNumber && (
              <p className="text-body-regular text-foreground-faint mb-1 flex-wrap">
                {preProcessedPayout?.details?.beneficiary?.bankAccount?.accountNumber}
              </p>
            ),
            rightLabel: t('beneficiaryPaymentReference'),
            rightDescription: preProcessedPayout?.paymentReference && (
              <p className="text-body-regular text-foreground-faint mb-1 flex-wrap break-words">
                {preProcessedPayout?.paymentReference}
              </p>
            ),
          }}
        />
      }
      body={
        <Box tag="section" width="full" background="standard">
          {isLoadingMassFiatPayoutDetail ? (
            <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
          ) : (
            <MassPayoutSidePanelBody sidePanelBodyData={sidePanelBodyData} isB2CPayout={isB2C} />
          )}
        </Box>
      }
    />
  )
}
